import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, GeneralDialog, InputWithLabel } from "@ptsd/uikit";
import i18next from "i18next";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Dialog, Form, View } from "tamagui";
import { z } from "zod";

import { api } from "~/utils/api";

const formSchema = z.object({
  name: z
    .string()
    .min(2, {
      message: i18next.t("groups:validation-errors.min-length"),
    })
    .max(50, {
      message: i18next.t("groups:validation-errors.max-length"),
    }),
});

type CreateNewGroupForm = z.infer<typeof formSchema>;

export const AddGroupDialog = () => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation("groups");

  const { control, reset, handleSubmit } = useForm<CreateNewGroupForm>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
    },
  });

  const utils = api.useUtils();

  const { mutate, isLoading } = api.group.create.useMutation({
    onSuccess: () => {
      void utils.group.list.invalidate();
      setOpen(false);
    },
  });

  const onSubmit = (values: CreateNewGroupForm) => {
    mutate({ name: values.name });
    reset();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Dialog.Trigger data-id="create-group-dialog-trigger" asChild>
        <Button variant="primary" size="md" rounded="lg">
          {t("create-group.trigger")}
        </Button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay
          zIndex={0}
          key={"add-group-dialog-overlay"}
          style={{
            backgroundColor: "#6A696966",
            backdropFilter: "blur(10px)",
          }}
        />
        <GeneralDialog.Content
          data-id="create-group-dialog-content"
          maxWidth={600}
          width="100%"
        >
          <Form onSubmit={handleSubmit(onSubmit)}>
            <View
              width="100%"
              gap={"$5"}
              alignItems="center"
              paddingTop={"$5"}
              paddingBottom={"$4"}
            >
              <GeneralDialog.Title data-id="create-group-dialog-title">
                {t("create-group.title")}
              </GeneralDialog.Title>

              <Controller
                control={control}
                name="name"
                render={({ field, fieldState }) => (
                  <InputWithLabel
                    data-id="create-group-input"
                    maxWidth={400}
                    variant="inline"
                    size="md"
                    rounded="md"
                    label="Назва групи"
                    placeholder={t("create-group.placeholder")}
                    isError={!!fieldState.error?.message}
                    error={fieldState.error?.message}
                    onChangeText={field.onChange}
                    {...field}
                  />
                )}
              />
            </View>

            <GeneralDialog.ControllArea
              gap={"$2"}
              alignItems="center"
              paddingVertical={"$3"}
            >
              <Form.Trigger asChild>
                <Button
                  data-id="create-group-submit-button"
                  variant="primary"
                  minWidth={180}
                  maxWidth={320}
                  width={"100%"}
                  size="md"
                  rounded="lg"
                >
                  {isLoading
                    ? t("create-group.submit.loading")
                    : t("create-group.submit.text")}
                </Button>
              </Form.Trigger>

              <GeneralDialog.Close data-id="create-group-cancel-button">
                {t("create-group.close")}
              </GeneralDialog.Close>
            </GeneralDialog.ControllArea>
          </Form>
        </GeneralDialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
};
